import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Box,
  Divider,
  Typography,
} from '@material-ui/core';
import {ArrowBackIos, ArrowForwardIos} from '@material-ui/icons';
import {makeStyles} from '@material-ui/core/styles';
import {parcelStatusOptions} from 'utils/helper';
import imagePlaceholder from 'assets/image-placeholder.png';
import type {FormattedDataType} from 'pages/Correct';

interface CorrectItemDialogProps {
  open: boolean;
  editedItems: FormattedDataType[];
  currentIndex: number;
  onClose: () => void;
  onSave: () => void;
  onPrev: () => void;
  onNext: () => void;
  onChange: (field: keyof FormattedDataType, value: string) => void;
  errors: {
    [id: string]: {
      code?: string | undefined;
    };
  };
  disableSave: boolean;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 12,
    paddingBottom: 10,
    maxWidth: 640,
    width: '100%',
    margin: '0 auto',
  },
  header: {
    fontFamily: 'SF Pro Display, sans-serif',
    fontWeight: 700,
    fontSize: 21,
    lineHeight: '32px',
    letterSpacing: 0,
    verticalAlign: 'middle',
  },
  mediaWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  media: {
    maxHeight: 300,
    objectFit: 'contain',
    borderRadius: 8,
    width: '100%',
    backgroundColor: '#f0f0f0',
  },
  arrowButton: {
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  cancelButton: {
    width: 83,
    height: 40,
    borderRadius: 4,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#9E9E9E',
    padding: '6px 16px',
    color: '#9E9E9E',
    backgroundColor: '#fff',
    textTransform: 'none',
    marginRight: 10,
  },
  saveButton: {
    width: 68,
    height: 40,
    borderRadius: 4,
    padding: '6px 16px',
    backgroundColor: '#36A6FF',
    color: '#fff',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#1e90ff',
    },
  },
  divider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    margin: '12px auto',
  },
  semiTransparentBg: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  slideContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  formRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    width: '100%',
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
  },
  textField: {
    flex: 1,
  },
}));

const CorrectItemDialog: React.FC<CorrectItemDialogProps> = ({
  open,
  editedItems,
  currentIndex,
  errors,
  onClose,
  onSave,
  onPrev,
  onNext,
  onChange,
  disableSave,
}) => {
  const classes = useStyles();
  const currentItem = editedItems[currentIndex];
  const currentErrors = errors[currentItem?.id] || {};

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      classes={{paper: classes.dialogPaper}}
    >
      <DialogTitle disableTypography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={classes.header}>Update Data</Box>
          <Box display="flex">
            <Button onClick={onClose} className={classes.cancelButton}>
              Cancel
            </Button>
            <Button
              onClick={onSave}
              variant="contained"
              className={classes.saveButton}
              disabled={disableSave}
            >
              Save
            </Button>
          </Box>
        </Box>
        <Divider className={classes.divider} />
      </DialogTitle>

      <DialogContent>
        {currentItem && (
          <Box className={classes.mediaWrapper}>
            {/* Prev Button */}
            <IconButton onClick={onPrev} className={classes.arrowButton}>
              <ArrowBackIos />
            </IconButton>

            {/* Slide Content (image + inputs) */}
            <Box className={classes.slideContent}>
              <img
                src={currentItem.image || imagePlaceholder}
                alt="stamp-free-code"
                className={classes.media}
              />

              <Box className={classes.formRow}>
                {/* StampFree Code */}
                <Box className={classes.fieldWrapper}>
                  <Typography variant="body2" className={classes.fieldLabel}>
                    StampFree Code
                  </Typography>
                  <TextField
                    value={currentItem.code || ''}
                    onChange={e => onChange('code', e.target.value)}
                    className={classes.textField}
                    error={!!currentErrors.code}
                    helperText={currentErrors.code}
                    variant="outlined"
                    size="small"
                    placeholder="StampFree Code"
                  />
                </Box>

                {/* Status */}
                <Box className={classes.fieldWrapper}>
                  <Typography variant="body2" className={classes.fieldLabel}>
                    Status
                  </Typography>
                  <TextField
                    select
                    value={currentItem.action}
                    onChange={e => onChange('action', e.target.value)}
                    className={classes.textField}
                    variant="outlined"
                    size="small"
                  >
                    {parcelStatusOptions.map((parcelStatus: any) => (
                      <MenuItem
                        value={parcelStatus.value}
                        key={parcelStatus.key}
                      >
                        {parcelStatus.text}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            </Box>

            {/* Next Button */}
            <IconButton onClick={onNext} className={classes.arrowButton}>
              <ArrowForwardIos />
            </IconButton>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CorrectItemDialog;
